/**
 * Shapes returned by the custom-domain endpoints
 * (`competition_insights.modules.tenancy.schemas`). As everywhere in this app,
 * nothing here names a tenant: the server reads it from the session.
 */

/** Mirrors `modules.tenancy.models.TenantDomainStatus`. */
export type DomainStatus = 'pending_dns' | 'verified' | 'active' | 'removed'

/** One record the customer publishes in their own DNS, rendered verbatim. */
export interface DnsRecord {
  type: 'CNAME' | 'TXT'
  name: string
  value: string
}

/** Mirrors `DomainCheckOut` — what the last DNS check saw. */
export interface DomainCheck {
  verified: boolean
  points_at_platform: boolean
  token_published: boolean
  observed_addresses: string[]
}

/** Mirrors `TenantDomainOut`. */
export interface TenantDomain {
  id: string
  hostname: string
  status: DomainStatus
  verification_token: string
  records: DnsRecord[]
  requested_at: string
  verified_at: string | null
  activated_at: string | null
  removed_at: string | null
}

/** Mirrors `TenantDomainStateOut` — the whole screen in one response. */
export interface DomainState {
  /**
   * The TmT-owned hostname, and the CNAME target. It travels with the response
   * because the browser has no other way to learn it: the host it was served on
   * is the tenant's own, which is the answer only once onboarding has succeeded.
   */
  platform_host: string
  domain: TenantDomain | null
  /** Present only on the response to a verification attempt. */
  check: DomainCheck | null
}

/** A domain that is not live yet, whatever the reason, still has work to do. */
export function isLive(domain: TenantDomain | null): boolean {
  return domain?.status === 'active'
}

/** Whether the DNS instructions are still worth showing. */
export function needsDns(domain: TenantDomain | null): boolean {
  return domain !== null && (domain.status === 'pending_dns' || domain.status === 'verified')
}

export const STATUS_LABELS: Record<DomainStatus, string> = {
  pending_dns: 'Waiting for DNS',
  verified: 'Verified',
  active: 'Live',
  removed: 'Removed',
}
